import _ from "lodash"

import endpoints from "@config/endpoints"
import { selectTeeupsState, selectArchivedTeeups } from "@selectors/teeups"
import { selectUnreadCount, selectNotifications } from "@selectors/notification"
import { formatSuggestion } from "@utils/gamePlanUtils"
import { rollupCountsByCategory } from "@utils/notificationCategories"
import {
  fetchNotifications,
  fetchUnreadCount,
  fetchUnreadCountByType,
  markNotificationRead,
  markAllNotificationsRead,
} from "@utils/notificationsApi"

import api from "../api"
import { store } from "../index"

import actionTypes from "./actionTypes"

const getNotifications = () => ({ type: actionTypes.GET_NOTIFICATIONS })
const gotNotifications = notifications => ({
  type: actionTypes.GOT_NOTIFICATIONS,
  payload: notifications,
})
const appendedNotificationsPage = notifications => ({
  type: actionTypes.APPENDED_NOTIFICATIONS_PAGE,
  payload: notifications,
})
const setNotificationsHasMore = hasMore => ({
  type: actionTypes.SET_NOTIFICATIONS_HAS_MORE,
  payload: hasMore,
})
const setCategoryCounts = counts => ({
  type: actionTypes.SET_NOTIFICATION_CATEGORY_COUNTS,
  payload: counts,
})
const setUnreadCount = count => ({
  type: actionTypes.SET_UNREAD_NOTIFICATIONS_COUNT,
  payload: count,
})
const readNotification = notification => ({
  type: actionTypes.READ_NOTIFICATION,
  payload: notification,
})
const removeNotification = notification => ({
  type: actionTypes.REMOVE_NOTIFICATION,
  payload: notification,
})
const updateNotificationLastSeen = () => ({
  type: actionTypes.UPDATE_NOTIFICATION_LAST_SEEN,
})
const startUserNotifications = () => ({
  type: actionTypes.NOTIFICATIONS_START,
})

// Refresh the bell badge (total unread) + per-category counts. Cheap (2 small COUNT queries),
// safe to call after any read/mark-all action so the rail badges never drift from the server.
const refreshUnreadCounts = () => async dispatch => {
  const [total, byType] = await Promise.all([fetchUnreadCount(), fetchUnreadCountByType()])
  dispatch(setUnreadCount(total))
  dispatch(setCategoryCounts(rollupCountsByCategory(byType)))
}

// wave4-notif-conform: notification-center feed, first page (resets the list). Badge = TOTAL
// unread (legacy spec conformance -- see api/routers/notifications.py module docstring); the
// panel itself lazy-loads additional pages via loadMoreNotifications below instead of a 30-day
// window.
export const loadNotificationCenter = () => async dispatch => {
  dispatch(getNotifications())
  try {
    const [{ items, hasMore }] = await Promise.all([
      fetchNotifications(0),
      dispatch(refreshUnreadCounts()),
    ])
    dispatch(gotNotifications(items))
    dispatch(setNotificationsHasMore(hasMore))
  } catch (error) {
    dispatch({ type: actionTypes.GET_NOTIFICATIONS_ERROR })
    // eslint-disable-next-line no-console
    console.warn("[notifications] loadNotificationCenter failed", error)
  }
}

// wave4-notif-conform: lazy-load pagination -- fetches the NEXT page and appends it (legacy spec:
// "There should be a pagination system with lazy load"). currentCount = how many rows are already
// loaded, used as the offset.
export const loadMoreNotifications = currentCount => async dispatch => {
  dispatch(getNotifications())
  try {
    const { items, hasMore } = await fetchNotifications(currentCount)
    dispatch(appendedNotificationsPage(items))
    dispatch(setNotificationsHasMore(hasMore))
  } catch (error) {
    dispatch({ type: actionTypes.GET_NOTIFICATIONS_ERROR })
    // eslint-disable-next-line no-console
    console.warn("[notifications] loadMoreNotifications failed", error)
  }
}

// Wave 3 NC1 / wave4-notif-conform: mark one notification read (server + local state),
// decrementing the TOTAL-unread badge by exactly 1 (not zeroing it -- legacy spec: badge is a
// running unread count, only drops as items are actually read).
export const markNotificationReadThunk = notificationId => async dispatch => {
  const ok = await markNotificationRead(notificationId)
  if (!ok) return
  dispatch(readNotification({ id: notificationId }))
  const current = selectUnreadCount(store.getState())
  dispatch(setUnreadCount(Math.max(0, current - 1)))
  dispatch(refreshUnreadCounts())
}

// wave4-notif-conform: explicit "Mark all as read" button ONLY (per legacy spec conformance --
// this is NO LONGER auto-fired on panel open, see NotificationBell/index.js). Marks every
// notification read server-side, then reloads the panel's first page + all counts so the UI
// reflects the sweep exactly (including any not-yet-loaded rows from lazy-load pages).
export const markAllNotificationsReadThunk = () => async dispatch => {
  const ok = await markAllNotificationsRead()
  if (!ok) return
  await dispatch(loadNotificationCenter())
}

// FIX-1 (inbox unread-dot, 2026-07-04): iPhone parity -- opening a TeeUp's detail clears ONLY
// that TeeUp's unread notifications (teeup-mobile's Teeup screen calls clearNotifications for the
// teeup on mount, see gitnexus UI-SCREEN-ATLAS ~line 537-539). Reuses the SAME persisted mark-read
// path the bell panel uses (markNotificationRead -> PUT /users/notifications/{id}/read) so this is
// not a parallel/duplicate mechanism, and updates local state (readNotification) so
// selectUnreadTeeupIds drops the id immediately -- the inbox activity dot clears on navigating
// back WITHOUT a refetch, and stays cleared after a full reload (server-persisted). Fire-and-forget
// from the caller (TeeUpPageContext) -- never blocks the detail page render.
export const markTeeupNotificationsReadOnOpenThunk = teeupId => async dispatch => {
  if (teeupId == null) return
  const idStr = String(teeupId)
  const unread = selectNotifications(store.getState()).filter(
    n => n && n.read === false && n.teeupId != null && String(n.teeupId) === idStr
  )
  if (!unread.length) return
  await Promise.all(
    unread.map(async n => {
      const ok = await markNotificationRead(n.id)
      if (ok) dispatch(readNotification({ id: n.id }))
    })
  )
  dispatch(refreshUnreadCounts())
}

export const loadNotifications = () => {
  const url = endpoints.teeup.teeupsState()

  return api.client
    .get(url)
    .then(({ data: response }) => {
      let teeupsToCheck = []
      let teeupsStateObj = {}

      const teeups = selectTeeupsState(store.getState())
      const archivedTeeups = selectArchivedTeeups(store.getState())
      const teeupsWithEvents = [...teeups, ...archivedTeeups]
      const teeupsState = response.map(r => {
        const event = {
          ...r,
          when: r.when ? formatSuggestion(r.when) : r.when,
        }
        const aTeeup = _.find(
          teeupsWithEvents,
          teeup => teeup.id === event.teeupId
        )
        if (aTeeup) {
          teeupsToCheck.push(aTeeup)
          teeupsStateObj[event.teeupId] = event
        }
        return event
      })
      store.dispatch({
        type: actionTypes.GOT_TEEUPS_STATE,
        payload: teeupsState,
      })
    })
    .catch(error => {
      console.log("Fetch teeups state error", error)
    })
}

export default {
  getNotifications,
  readNotification,
  removeNotification,
  updateNotificationLastSeen,
  startUserNotifications,
  loadNotificationCenter,
  loadMoreNotifications,
  markNotificationReadThunk,
  markAllNotificationsReadThunk,
  markTeeupNotificationsReadOnOpenThunk,
}
